import { supabase } from '@/core/database/supabase';
import { enforceSettingsPermission } from '@/modules/settings/permissions/enforceSettingsPermission';
import type { ActivityLog, ActivityFilters } from '../types/activityList.types';

export const activityListService = {
  async getLogs(companyId: string, filters: ActivityFilters): Promise<{ data: ActivityLog[], count: number }> {
    await enforceSettingsPermission('settings.activity.view', { companyId });
    
    const from = (filters.page - 1) * filters.pageSize;
    const to = from + filters.pageSize - 1;

    let query = supabase
      .from('audit_logs')
      .select(`
        id, 
        summary, 
        action_code, 
        module_code, 
        entity_type,
        entity_id,
        before_data,
        after_data,
        ip_address,
        user_agent,
        browser,
        os,
        device_type,
        created_at, 
        actor:user_profiles!actor_user_id(full_name, email)
      `, { count: 'exact' })
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (filters.module && filters.module !== 'all') {
      query = query.eq('module_code', filters.module);
    }

    if (filters.action && filters.action !== 'all') {
      query = query.eq('action_code', filters.action);
    }

    if (filters.userId && filters.userId !== 'all') {
      query = query.eq('actor_user_id', filters.userId);
    }

    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }

    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    if (filters.query) {
      query = query.ilike('summary', `%${filters.query}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('[ActivityService] error:', error);
      return { data: [], count: 0 };
    }

    const mappedData = (data || []).map((log: any) => ({
      id: log.id,
      actorName: log.actor?.full_name || 'Hệ thống',
      actorEmail: log.actor?.email || '',
      actionCode: log.action_code,
      moduleCode: log.module_code,
      entityType: log.entity_type,
      entityId: log.entity_id,
      summary: log.summary || log.action_code,
      createdAt: log.created_at,
      beforeData: log.before_data,
      afterData: log.after_data,
      ipAddress: log.ip_address,
      userAgent: log.user_agent,
      browser: log.browser,
      os: log.os,
      deviceType: log.device_type,
    }));

    return { data: mappedData, count: count || 0 };
  },

  async getUsers(companyId: string) {
    const { data } = await supabase
      .from('user_profiles')
      .select('id, full_name')
      .eq('company_id', companyId)
      .order('full_name');
    return data || [];
  }
};
